/**
 * Copyright © Binary Anvil, Inc. All rights reserved.
 * See https://binaryanvil.com/software/license for license details.
 */

import productsConfig from "@/app/config/products.config";
import parse from "html-react-parser";
import {allowedCurrencies} from "@/app/config/allowedCurrencies";
import {LOCALE_COOKIE, PRICE_TEMPLATE_CONSTANTS} from "@/app/constants";
import {Maybe, CurrencyEnum} from "@/generated/graphql";
import {getCookie} from "@/app/utils/cookie";

/**
 * Format price using default format
 *
 * @param options
 */
export const priceFormatter = (options: {
    price?: Maybe<string | number>,
    rangePriceValueMax?: string | number,
    oldPrice?: string | number,
    rangeOldPriceValueMax?: string | number,
    currencySymbolCode?: CurrencyEnum | string | null| undefined,
    priceTemplate?:string,
    fractionDigits?: number,
}) => {
    const currentLocale = getCookie({name:LOCALE_COOKIE});
    const currencySymbol = options.currencySymbolCode && allowedCurrencies[options.currencySymbolCode] &&
    allowedCurrencies[options.currencySymbolCode].symbol ?
        allowedCurrencies[options.currencySymbolCode].symbol :
        options.currencySymbolCode ?? '';

    const fractionDigits = options.fractionDigits ?? productsConfig.priceDecimals;

    // OLD price for products on sale
    let priceOld = options.oldPrice && options.price ?
        parseFloat(String(options.oldPrice)).toFixed(fractionDigits).toString() : '';
    // CURRENT final product price (regular or discount price)
    let priceFinal = options.price || typeof options.price === "number" ? parseFloat(String(options.price)).toFixed(fractionDigits).toString() :
        options.oldPrice ? parseFloat(String(options.oldPrice)).toFixed(fractionDigits).toString() : false;

    // OLD price for products on sale with PRICE RANGE
    let priceRangeMaxOld = options.rangeOldPriceValueMax && options.rangePriceValueMax ?
        parseFloat(String(options.rangeOldPriceValueMax)).toFixed(fractionDigits).toString() : '';
    // CURRENT final price (regular or discount price) for product with PRICE RANGE
    let priceRangeMaxFinal = options.rangePriceValueMax ? parseFloat(String(options.rangePriceValueMax)).toFixed(fractionDigits).toString() :
        options.rangeOldPriceValueMax ? parseFloat(String(options.rangeOldPriceValueMax)).toFixed(fractionDigits).toString() : '';

    // Check if old and current prices are the same, so no discount actually
    priceOld = priceOld === priceFinal && priceRangeMaxOld === priceRangeMaxFinal ? '' : priceOld;

    let priceHtml = '';

    const priceTemplateKey = currentLocale && typeof productsConfig.priceFormatDiscount[currentLocale] !== "undefined" ?
        currentLocale : 'default';
    const priceType = 'default';

    const template = options.priceTemplate ? options.priceTemplate : // Checking if custom price template was passed
            priceOld && priceFinal ? // Checking if price has a discount
                productsConfig.priceFormatDiscount[priceTemplateKey][priceType] :
                productsConfig.priceFormatDefault[priceTemplateKey][priceType];

    // TODO uncomment if you need comma divider
    // const addCommaDivider = (value:string) => value.replace(/\d(?=(\d{3})+\.)/g, '$&,');

    if (priceFinal) {
        priceHtml = template.replaceAll(PRICE_TEMPLATE_CONSTANTS.currencySymbol, currencySymbol)
            .replaceAll(PRICE_TEMPLATE_CONSTANTS.oldPrice, priceOld)
            .replaceAll(PRICE_TEMPLATE_CONSTANTS.finalPrice, priceFinal)
            .replaceAll(PRICE_TEMPLATE_CONSTANTS.oldPriceMax, priceRangeMaxOld)
            .replaceAll(PRICE_TEMPLATE_CONSTANTS.finalPriceMax, priceRangeMaxFinal);
            // TODO uncomment if you need comma divider
            // .replaceAll(PRICE_TEMPLATE_CONSTANTS.oldPrice, addCommaDivider(priceOld))
            // .replaceAll(PRICE_TEMPLATE_CONSTANTS.finalPrice, addCommaDivider(priceFinal))
            // .replaceAll(PRICE_TEMPLATE_CONSTANTS.oldPriceMax, addCommaDivider(priceRangeMaxOld))
            // .replaceAll(PRICE_TEMPLATE_CONSTANTS.finalPriceMax, addCommaDivider(priceRangeMaxFinal));


        /* TODO Alternative way of formatting price using Intl.NumberFormat could be used in this util if needed
        new Intl.NumberFormat(locale || storeConfig.locale.split('_').join('-'), {
            style: 'currency',
            currency: currencySymbol
        }).format(priceFinal) */
    }

    return parse(priceHtml);
};